import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HiChevronDown, HiX, HiCalendar } from 'react-icons/hi';
import classNames from 'classnames';
import { AppDispatch, RootState } from '../../state/store';
import { fetchDashboardCampaigns } from '../../services/DashboardService';

// ── Date utilities ───────────────────────────────────────────────────────────

export const formatDashboardDate = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const fmt = formatDashboardDate;

export type DatePreset =
  | 'this_week'
  | 'last_week'
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'last_quarter'
  | 'this_year'
  | 'last_year'
  | 'custom';

export function getPresetRange(preset: DatePreset): { fromDate: string; toDate: string } {
  const now = new Date();
  const today = fmt(now);

  switch (preset) {
    case 'this_week': {
      const mon = new Date(now);
      mon.setDate(now.getDate() - ((now.getDay() + 6) % 7));
      return { fromDate: fmt(mon), toDate: today };
    }
    case 'last_week': {
      const mon = new Date(now);
      mon.setDate(now.getDate() - ((now.getDay() + 6) % 7) - 7);
      const sun = new Date(mon);
      sun.setDate(mon.getDate() + 6);
      return { fromDate: fmt(mon), toDate: fmt(sun) };
    }
    case 'this_month':
      return { fromDate: fmt(new Date(now.getFullYear(), now.getMonth(), 1)), toDate: today };
    case 'last_month': {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      return { fromDate: fmt(first), toDate: fmt(last) };
    }
    case 'this_quarter': {
      const q = Math.floor(now.getMonth() / 3);
      return { fromDate: fmt(new Date(now.getFullYear(), q * 3, 1)), toDate: today };
    }
    case 'last_quarter': {
      const q = Math.floor(now.getMonth() / 3);
      const prevQ = q === 0 ? 3 : q - 1;
      const year = q === 0 ? now.getFullYear() - 1 : now.getFullYear();
      const first = new Date(year, prevQ * 3, 1);
      const last = new Date(year, prevQ * 3 + 3, 0);
      return { fromDate: fmt(first), toDate: fmt(last) };
    }
    case 'this_year':
      return { fromDate: `${now.getFullYear()}-01-01`, toDate: today };
    case 'last_year': {
      const y = now.getFullYear() - 1;
      return { fromDate: `${y}-01-01`, toDate: `${y}-12-31` };
    }
    case 'custom':
      return { fromDate: '', toDate: '' };
  }
}

const PRESETS: { key: DatePreset; label: string }[] = [
  { key: 'this_week', label: 'This week' },
  { key: 'last_week', label: 'Last week' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'this_quarter', label: 'This quarter' },
  { key: 'last_quarter', label: 'Last quarter' },
  { key: 'this_year', label: 'This year' },
  { key: 'last_year', label: 'Last year' },
  { key: 'custom', label: 'Custom range' },
];

// ── Component ────────────────────────────────────────────────────────────────

export type DashboardFiltersState = {
  fromDate: string;
  toDate: string;
  activePreset: DatePreset;
  selectedCampaignIds: number[];
};

type Props = {
  filters: DashboardFiltersState;
  onChange: (next: DashboardFiltersState) => void;
};

export default function DashboardFilterBar({ filters, onChange }: Props) {
  const dispatch = useDispatch<AppDispatch>();
  const { campaignsList, campaignsListLoading } = useSelector((state: RootState) => state.dashboard);

  const [campaignOpen, setCampaignOpen] = useState(false);
  const [customDateOpen, setCustomDateOpen] = useState(filters.activePreset === 'custom');
  const [customFromDate, setCustomFromDate] = useState(filters.fromDate);
  const [customToDate, setCustomToDate] = useState(filters.toDate);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const datePickerRef = useRef<HTMLDivElement>(null);

  // Load campaigns once for the dropdown
  useEffect(() => {
    dispatch(fetchDashboardCampaigns());
  }, [dispatch]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setCampaignOpen(false);
      }
      if (datePickerRef.current && !datePickerRef.current.contains(e.target as Node)) {
        if (filters.activePreset === 'custom') {
          setCustomDateOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [filters.activePreset]);

  const selectPreset = (preset: DatePreset) => {
    if (preset === 'custom') {
      setCustomDateOpen(true);
      setCustomFromDate(filters.fromDate || fmt(new Date()));
      setCustomToDate(filters.toDate || fmt(new Date()));
      onChange({ ...filters, activePreset: preset });
    } else {
      const { fromDate, toDate } = getPresetRange(preset);
      onChange({ ...filters, fromDate, toDate, activePreset: preset });
      setCustomDateOpen(false);
    }
  };

  const applyCustomRange = () => {
    if (customFromDate && customToDate) {
      onChange({
        ...filters,
        fromDate: customFromDate,
        toDate: customToDate,
        activePreset: 'custom',
      });
      setCustomDateOpen(false);
    }
  };

  const toggleCampaign = (id: number) => {
    const next = filters.selectedCampaignIds.includes(id)
      ? filters.selectedCampaignIds.filter((c) => c !== id)
      : [...filters.selectedCampaignIds, id];
    onChange({ ...filters, selectedCampaignIds: next });
  };

  const clearCampaigns = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange({ ...filters, selectedCampaignIds: [] });
  };

  const campaignLabel =
    filters.selectedCampaignIds.length === 0
      ? 'All campaigns'
      : `${filters.selectedCampaignIds.length} campaign${filters.selectedCampaignIds.length > 1 ? 's' : ''} selected`;

  return (
    <div className="relative z-50 flex flex-wrap items-center gap-3 rounded-2xl border border-white/60 dark:border-white/10 bg-white/75 dark:bg-slate-900/60 backdrop-blur-sm px-4 py-3 shadow-sm">
      {/* Date preset chips */}
      <div className="flex flex-wrap items-center gap-1.5">
        {PRESETS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => selectPreset(key)}
            className={classNames(
              'rounded-xl px-3 py-1.5 text-xs font-semibold tracking-wide transition-colors',
              filters.activePreset === key
                ? 'bg-primary-100 text-white shadow-sm dark:bg-primary-70'
                : 'bg-white/60 dark:bg-slate-800/60 text-gray-600 dark:text-gray-300 hover:bg-primary-10 dark:hover:bg-slate-700/70 border border-white/60 dark:border-white/10',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Divider */}
      <div className="hidden h-6 w-px bg-gray-200 dark:bg-white/10 sm:block" />

      {/* Date range display / custom date picker */}
      <div ref={datePickerRef} className="relative">
        <button
          type="button"
          onClick={() => setCustomDateOpen(filters.activePreset === 'custom' ? !customDateOpen : false)}
          className={classNames(
            'flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-medium transition-colors',
            filters.activePreset === 'custom' && customDateOpen
              ? 'border-primary-100 bg-primary-10 text-primary-100 dark:border-primary-70 dark:bg-primary-70/20 dark:text-primary-10'
              : 'border-white/60 dark:border-white/10 bg-white/60 dark:bg-slate-800/60 text-gray-700 dark:text-gray-300',
          )}
        >
          <HiCalendar className="h-4 w-4" />
          <span>{filters.fromDate}</span>
          <span className="text-gray-400">→</span>
          <span>{filters.toDate}</span>
        </button>

        {/* Custom date picker dropdown */}
        {filters.activePreset === 'custom' && customDateOpen && (
          <div className="absolute left-0 top-full z-50 mt-1.5 w-72 rounded-2xl border border-white/60 dark:border-white/10 bg-white dark:bg-slate-900 shadow-xl backdrop-blur-sm p-4">
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300 mb-1">
                  From date
                </label>
                <input
                  type="date"
                  value={customFromDate}
                  onChange={(e) => setCustomFromDate(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 dark:border-white/15 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 outline-none focus:border-primary-100 dark:focus:border-primary-70"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300 mb-1">
                  To date
                </label>
                <input
                  type="date"
                  value={customToDate}
                  onChange={(e) => setCustomToDate(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 dark:border-white/15 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 outline-none focus:border-primary-100 dark:focus:border-primary-70"
                />
              </div>
              <button
                type="button"
                onClick={applyCustomRange}
                disabled={!customFromDate || !customToDate}
                className="w-full rounded-xl bg-primary-100 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-primary-70 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-primary-70 dark:hover:bg-primary-100"
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="hidden h-6 w-px bg-gray-200 dark:bg-white/10 sm:block" />

      {/* Campaign multi-select */}
      <div ref={dropdownRef} className="relative">
        <button
          type="button"
          onClick={() => setCampaignOpen((o) => !o)}
          className="flex items-center gap-2 rounded-xl border border-white/60 dark:border-white/10 bg-white/60 dark:bg-slate-800/60 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-200 transition-colors hover:bg-primary-10 dark:hover:bg-slate-700/70"
        >
          <span>{campaignLabel}</span>
          {filters.selectedCampaignIds.length > 0 && (
            <span
              role="button"
              tabIndex={0}
              onClick={clearCampaigns}
              onKeyDown={(e) => { if (e.key === 'Enter') clearCampaigns(e as any); }}
              className="ml-0.5 rounded-full hover:text-red-500"
              aria-label="Clear campaign filter"
            >
              <HiX className="h-3 w-3" />
            </span>
          )}
          <HiChevronDown
            className={classNames('h-3.5 w-3.5 transition-transform', campaignOpen && 'rotate-180')}
          />
        </button>

        {campaignOpen && (
          <div className="absolute left-0 top-full z-40 mt-1.5 max-h-64 w-64 overflow-y-auto rounded-2xl border border-white/60 dark:border-white/10 bg-white dark:bg-slate-900 shadow-xl backdrop-blur-sm">
            {campaignsListLoading ? (
              <div className="px-4 py-6 text-center text-xs text-gray-500 dark:text-gray-400">
                Loading campaigns…
              </div>
            ) : campaignsList.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-gray-500 dark:text-gray-400">
                No campaigns found
              </div>
            ) : (
              <>
                {/* "All" option */}
                <button
                  type="button"
                  onClick={() => onChange({ ...filters, selectedCampaignIds: [] })}
                  className={classNames(
                    'flex w-full items-center gap-2 border-b border-gray-100 dark:border-white/10 px-4 py-2.5 text-left text-xs font-semibold transition-colors hover:bg-primary-10 dark:hover:bg-slate-800',
                    filters.selectedCampaignIds.length === 0 ? 'text-primary-100 dark:text-primary-10' : 'text-gray-700 dark:text-gray-200',
                  )}
                >
                  <span className={classNames(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                    filters.selectedCampaignIds.length === 0
                      ? 'border-primary-100 bg-primary-100 text-white'
                      : 'border-gray-300 dark:border-white/20',
                  )}>
                    {filters.selectedCampaignIds.length === 0 && (
                      <svg className="h-2.5 w-2.5" viewBox="0 0 10 10" fill="none">
                        <path d="M2 5l2.5 2.5L8 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </span>
                  All campaigns
                </button>

                {campaignsList.map((c) => {
                  const checked = filters.selectedCampaignIds.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggleCampaign(c.id)}
                      className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-xs transition-colors hover:bg-primary-10 dark:hover:bg-slate-800"
                    >
                      <span className={classNames(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                        checked
                          ? 'border-primary-100 bg-primary-100 text-white'
                          : 'border-gray-300 dark:border-white/20',
                      )}>
                        {checked && (
                          <svg className="h-2.5 w-2.5" viewBox="0 0 10 10" fill="none">
                            <path d="M2 5l2.5 2.5L8 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </span>
                      <span className="truncate text-gray-700 dark:text-gray-200">{c.name}</span>
                    </button>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
